import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { paymentReceiptUseService } from "../services/paymentReceiptUseService";
import { paymentReceiptUseDisplayId } from "./PaymentReceiptUseCommonStore";
import { PaymentReceiptUse } from "../../../api/graphql";
import { signedEditionDisplayId } from "../../signedEdition/store/SignedEditionCommonStore";
import { signedEditionService } from "../../signedEdition/services/signedEditionService";
import { InitializeParams } from "../../../store/types";

export class PaymentReceiptUseViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = paymentReceiptUseService;
  paymentReceiptUseInstance: PaymentReceiptUse | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  detailValue: string = "";
  usedAtValue: Date | null = null;
  signedEditionService = signedEditionService;
  signedEditionSearchValue: string = "";
  signedEditionIdValue: string = "";
  signedEditionSelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPaymentReceiptUseById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.paymentReceiptUseOne({ id });
    this.paymentReceiptUseInstance = result.paymentReceiptUseOne;
    if (this.paymentReceiptUseInstance) {
      this.loadValues(this.paymentReceiptUseInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.paymentReceiptUseInstance) {
      return paymentReceiptUseDisplayId(
        this.paymentReceiptUseInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PaymentReceiptUseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "signedEdition") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.signedEditionOne({
          id: params.returnData.resultId,
        });
        const signedEdition = result.signedEditionOne;
        this.signedEditionSelectedValue = signedEdition;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get signedEditionValue() {
    if (this.signedEditionSelectedValue) {
      return {
        id: this.signedEditionSelectedValue.id,
        displayId: signedEditionDisplayId(
          this.signedEditionSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.paymentReceiptUseInstance = null;
    this.detailValue = "";
    this.usedAtValue = null;
    this.signedEditionSearchValue = "";
    this.signedEditionIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPaymentReceiptUse() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editPaymentReceiptUse() {
    const id = this.paymentReceiptUseInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/paymentReceiptUse/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPaymentReceiptUse(handlePrint: () => void): void {
    handlePrint();
  }

  *closePaymentReceiptUse(): any {
    yield this.close();
  }

  loadValues(instance: PaymentReceiptUse) {
    this.idValue = instance.id;
    this.detailValue = instance.detail;
    this.usedAtValue = new Date(instance.usedAt);
    this.signedEditionSelectedValue = instance.signedEdition
      ? instance.signedEdition
      : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPaymentReceiptUseById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
